import {
  Brain,
  Briefcase,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderOpen,
  PieChart,
  FileTextIcon,
  LifeBuoyIcon,
  PanelLeftIcon,
  PanelRightIcon,
  SendIcon,
  Settings as SettingsIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { isBrainDemoEnabled } from "@/screens/BrainDemo/flag";

export type NavigationSubItem = {
  id: string;
  label: string;
  path: string;
  isActive?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
};

export type NavigationItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  path?: string;
  hasSubmenu: boolean;
  isExpanded?: boolean;
  adminOnly?: boolean;
  submenuItems?: NavigationSubItem[];
};

export const navigationItems: NavigationItem[] = [
  {
    id: "invoices",
    label: "Invoices",
    icon: FileTextIcon,
    path: "/invoices/dashboard",
    hasSubmenu: true,
    isExpanded: true,
    submenuItems: [
      { id: "process-invoices", label: "Processing", path: "/invoices/processing", isActive: false },
      { id: "matching", label: "Reconciliation", path: "/invoices/matching", isActive: false }
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: PieChart,
    path: "/reports",
    hasSubmenu: true,
    isExpanded: true,
    submenuItems: [
      { id: "new-report", label: "New Report", path: "/reports" },
    ],
    // Additional conversations are loaded dynamically
  },
  {
    id: "clients",
    label: "Clients",
    icon: Briefcase,
    path: "/clients",
    hasSubmenu: false,
  },
  {
    id: "files",
    label: "Files",
    icon: FolderOpen,
    path: "/files",
    hasSubmenu: true,
    isExpanded: false,
    submenuItems: [
      { id: "file-explorer", label: "Explorer", path: "/files" },
      { id: "tax-requests", label: "Tax Requests", path: "/files/tax-requests" },
    ],
  },
  ...(isBrainDemoEnabled
    ? [
        {
          id: "brain-demo",
          label: "Company brain",
          icon: Brain,
          path: "/demo/brain",
          hasSubmenu: true,
          isExpanded: true,
          submenuItems: [
            { id: "brain-chat", label: "Чат", path: "/demo/brain" },
            { id: "brain-decisions", label: "Решения", path: "/demo/decisions" },
          ],
        } satisfies NavigationItem,
      ]
    : []),
  {
    id: "settings",
    label: "Settings",
    icon: SettingsIcon,
    path: "/settings/profile",
    hasSubmenu: true,
    isExpanded: false,
    submenuItems: [
      { id: "settings-profile", label: "Profile", path: "/settings/profile" },
      { id: "settings-ai-instructions", label: "AI Instructions", path: "/settings/ai-instructions", adminOnly: true },
      { id: "settings-prompts", label: "System Prompts", path: "/settings/prompts", superAdminOnly: true },
      { id: "settings-admin", label: "Users", path: "/settings/admin", adminOnly: true },
      { id: "settings-learnings", label: "Rules & Mappings", path: "/settings/learnings" },
    ],
  },
];

export const navigationIcons = {
  ChevronDownIcon,
  ChevronRightIcon,
  PanelLeftIcon,
  PanelRightIcon,
  LifeBuoyIcon,
  SendIcon,
};

export type BreadcrumbItem = {
  label: string;
  path?: string;
};

const normalizePath = (pathname: string): string => {
  const [pathWithoutQuery] = pathname.split("?");
  if (pathWithoutQuery === "/") return "/";
  const withoutTrailing = pathWithoutQuery.replace(/\/+$/, "");
  return withoutTrailing || "/";
};

const specialBreadcrumbs: Array<{
  match: (path: string) => boolean;
  breadcrumbs: BreadcrumbItem[];
}> = [
    {
      match: (path) => path === "/invoices/dashboard",
      breadcrumbs: [
        { label: "Invoices", path: "/invoices/dashboard" },
        { label: "Dashboard" },
      ],
    },
    {
      match: (path) => path === "/process",
      breadcrumbs: [
        { label: "Invoices", path: "/invoices/dashboard" },
        { label: "Document Processing" },
      ],
    },
    {
      match: (path) => path.startsWith("/review"),
      breadcrumbs: [
        { label: "Invoices", path: "/invoices/dashboard" },
        { label: "Review" },
      ],
    },
    {
      match: (path) => path === "/reconciliation",
      breadcrumbs: [
        { label: "Invoices", path: "/invoices/dashboard" },
        { label: "Reconciliation" },
      ],
    },
  ];

export const getBreadcrumbsForPath = (pathname: string): BreadcrumbItem[] => {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === "/") {
    return [];
  }

  for (const { match, breadcrumbs } of specialBreadcrumbs) {
    if (match(normalizedPath)) {
      return breadcrumbs;
    }
  }

  for (const item of navigationItems) {
    if (item.submenuItems) {
      for (const subItem of item.submenuItems) {
        const subItemPath = normalizePath(subItem.path);
        if (
          subItemPath === normalizedPath ||
          normalizedPath.startsWith(`${subItemPath}/`)
        ) {
          const breadcrumbs: BreadcrumbItem[] = [];
          breadcrumbs.push({ label: item.label, path: item.path });

          breadcrumbs.push({
            label: subItem.label,
            path: subItem.path.includes("?") ? subItem.path.split("?")[0] : subItem.path,
          });

          return breadcrumbs;
        }
      }
    }

    if (item.path && normalizePath(item.path) === normalizedPath) {
      return [
        { label: item.label, path: item.path },
      ];
    }
  }

  const segments = normalizedPath.split("/").filter(Boolean);
  if (segments.length) {
    return segments.map((segment, index) => {
      const path = `/${segments.slice(0, index + 1).join("/")}`;
      return {
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        path: index === segments.length - 1 ? undefined : path,
      };
    });
  }

  return [];
};
